import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, RefreshCw } from 'lucide-react';
import api from '../lib/api';

const STATUS_COLORS = {
  sent: 'bg-[#5856D6]/12 text-[#3D3BAD]',
  opened: 'bg-[#FFC107]/15 text-[#7A4F00]',
  registered: 'bg-[#B6E600]/20 text-[#3F5200]',
  expired: 'bg-[#FF3B30]/12 text-[#FF3B30]',
};

export default function AdminVenueInvites() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = filter ? `?status=${filter}` : '';
      const [s, i] = await Promise.all([
        api.get('/venue-invites/admin/stats'),
        api.get(`/venue-invites/admin/all${q}`),
      ]);
      setStats(s.data);
      setInvites(i.data.invites || []);
    } catch {}
    setLoading(false);
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="content-area pb-12" data-testid="admin-venue-invites-page">
      <div className="px-4 pt-5 pb-3 flex items-center gap-3">
        <button data-testid="admin-vi-back" onClick={() => navigate(-1)} aria-label="Back">
          <ArrowLeft size={20} className="text-[#111]" />
        </button>
        <h1 className="text-lg font-bold tracking-tight text-[#111111] flex-1">Venue invites</h1>
        <button data-testid="admin-vi-refresh" onClick={load} aria-label="Refresh">
          <RefreshCw size={18} className={`text-[#9B9EA3] ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="px-4 space-y-4">
        {stats && (
          <section data-testid="admin-vi-stats" className="grid grid-cols-3 gap-2">
            <Stat label="Total" value={stats.total} testId="admin-vi-stat-total" />
            <Stat label="Sent" value={stats.sent} testId="admin-vi-stat-sent" />
            <Stat label="Registered" value={stats.registered} testId="admin-vi-stat-registered" />
            <Stat label="Conversion" value={`${stats.conversion_rate}%`} testId="admin-vi-stat-conversion" />
            <Stat label="Opened" value={stats.opened} testId="admin-vi-stat-opened" />
            <Stat label="Expired" value={stats.expired} testId="admin-vi-stat-expired" />
          </section>
        )}

        <div className="flex flex-wrap gap-1.5" data-testid="admin-vi-filters">
          {[['', 'All'], ['sent', 'Sent'], ['opened', 'Opened'], ['registered', 'Registered'], ['expired', 'Expired']].map(([k, label]) => (
            <button
              key={k || 'all'}
              data-testid={`admin-vi-filter-${k || 'all'}`}
              onClick={() => setFilter(k)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium ${filter === k ? 'bg-[#111] text-white' : 'bg-[#F1F0EA] text-[#6B6E73] border border-black/[0.06]'}`}
            >
              {label}
            </button>
          ))}
        </div>

        <section data-testid="admin-vi-list">
          {invites.length === 0 ? (
            <div data-testid="admin-vi-empty" className="bg-white rounded-2xl border border-black/[0.06] p-5 text-center">
              <Mail size={20} className="mx-auto text-[#B0B3B8] mb-2" />
              <p className="text-sm text-[#6B6E73]">No invites yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {invites.map(inv => (
                <div key={inv.id} data-testid={`admin-vi-row-${inv.id}`} className="bg-white rounded-2xl border border-black/[0.06] p-4">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-extrabold text-[#111] truncate">{inv.venue_name}</p>
                      <p className="text-xs text-[#6B6E73] truncate">{inv.contact_email}</p>
                    </div>
                    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full tracking-wider ${STATUS_COLORS[inv.status] || 'bg-[#F1F0EA] text-[#6B6E73]'}`}>
                      {inv.status}
                    </span>
                  </div>
                  <div className="mt-2 text-[11px] text-[#9B9EA3] flex flex-wrap gap-x-3 gap-y-1">
                    <span>Invited by {inv.inviter?.display_name || '—'}</span>
                    <span>{new Date(inv.created_at).toLocaleDateString()}</span>
                    {inv.converted_at && <span className="text-[#3F5200]">Converted</span>}
                  </div>
                  {inv.personal_note && (
                    <p className="mt-2 text-xs text-[#2A2A2A] italic">"{inv.personal_note}"</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function Stat({ label, value, testId }) {
  return (
    <div data-testid={testId} className="bg-white rounded-2xl border border-black/[0.06] p-3 text-center">
      <p className="text-lg font-extrabold text-[#111]">{value}</p>
      <p className="text-[10px] uppercase tracking-wider text-[#9B9EA3] mt-0.5">{label}</p>
    </div>
  );
}
